var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * A library for working with SockJS websockets.
 *
 * This is somewhat based on rxjs-websockets but with a lot of customisations
 * for freelancer.
 */
import { NgZone } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { TimeUtils } from '@freelancer/time-utils';
import { toNumber } from '@freelancer/utils';
import { Store } from '@ngrx/store';
import * as Rx from 'rxjs';
import { distinctUntilChanged, first, map, mapTo, pairwise, tap, withLatestFrom, } from 'rxjs/operators';
import { setDiff } from '../helpers';
import { startWithEmptyList } from '../operators';
import { MultiSet } from './multiset';
import { isWebsocketMessage, } from './server-event.model';
import { generateChannelSubMessage, generateHeader, generateOnlineOfflineSub, generateOnlineOfflineUnsub, Socket, } from './sock-js';
var WebSocketService = /** @class */ (function () {
    function WebSocketService(datastoreConfig, auth, store$, timeUtils, ngZone) {
        this.datastoreConfig = datastoreConfig;
        this.auth = auth;
        this.store$ = store$;
        this.timeUtils = timeUtils;
        this.ngZone = ngZone;
        this.connectionStatus$ = new Rx.BehaviorSubject(0);
        // FIXME: Make private and expose a public observable
        this.fromServerStream$ = new Rx.Subject();
        this.open = false;
        this.channelSubscriptions = new Set();
        this.onlineOfflineSubscriptions = new MultiSet();
        this.connectRetryCount = 0;
        this.create();
    }
    WebSocketService.prototype.create = function () {
        var _this = this;
        this.auth.authState$.pipe(distinctUntilChanged()).subscribe(function (auth) {
            _this.open = false;
            // FIXME: handle logged-out / logging-out
            if (!auth) {
                return;
            }
            var closed = function () {
                if (!_this.open) {
                    return;
                }
                _this.connectionStatus$.next(_this.connectionStatus$.getValue() - 1);
                _this.open = false;
            };
            _this.ngZone.runOutsideAngular(function () {
                _this.sock = new Socket(_this.datastoreConfig.webSocketUrl);
                _this.sock.onopen = function () {
                    _this.ngZone.run(function () {
                        _this.open = true;
                        _this.connectRetryCount = 0;
                        _this.connectionStatus$.next(_this.connectionStatus$.getValue() + 1);
                        _this.sendEvent(generateHeader(auth));
                        _this.subscribeToEvents();
                    });
                };
                // Only send events with actual messages, not handshaking messages.
                _this.sock.onmessage = function (event) {
                    _this.ngZone.run(function () {
                        var decodedEvent = JSON.parse(event.data);
                        if (isWebsocketMessage(decodedEvent)) {
                            _this.processEvent(decodedEvent, auth.userId);
                        }
                    });
                };
                _this.sock.onclose = function (event) {
                    _this.ngZone.run(function () {
                        closed();
                        if (event.wasClean) {
                            _this.fromServerStream$.complete();
                        }
                        else {
                            // FIXME: use global error toast service to display something when
                            // the websocket is reconnecting (and let the user force reconnect?)
                            var retryDelay = Math.pow(2, _this.connectRetryCount) * 1000 +
                                Math.round(Math.random() * 1000);
                            console.warn("websocket disconnected, retrying in " + retryDelay + "ms");
                            // race between the retry timeout & the online event..
                            Rx.race(_this.timeUtils.rxTimer(retryDelay).pipe(tap(function () {
                                // retryDelay is capped to ~60 seconds
                                if (_this.connectRetryCount <= 5) {
                                    _this.connectRetryCount++;
                                }
                                console.warn('reconnecting (timeout)...');
                            }), mapTo(true)), Rx.fromEvent(window, 'online').pipe(tap(function () {
                                _this.connectRetryCount = 0;
                                console.warn('reconnecting (online event)...');
                            }), mapTo(true)))
                                // ..take the first one..
                                .pipe(first())
                                // and re-create the websocket.
                                // FIXME
                                // tslint:disable-next-line: rxjs-no-nested-subscribe
                                .subscribe(function () {
                                _this.ngZone.runOutsideAngular(function () {
                                    _this.create();
                                });
                            });
                        }
                    });
                };
            });
            return function () {
                if (_this.sock) {
                    closed();
                    _this.ngZone.runOutsideAngular(function () {
                        _this.sock.close();
                    });
                }
            };
        });
    };
    WebSocketService.prototype.sendEvent = function (message) {
        var _this = this;
        if (this.open) {
            this.ngZone.runOutsideAngular(function () {
                _this.sock.send(message);
            });
        }
        else {
            console.error('Tried sending a message to a closed websocket.');
        }
    };
    WebSocketService.prototype.processEvent = function (event, toUserId) {
        var body = event.body;
        var action = {
            type: 'WS_MESSAGE',
            no_persist: event.body.no_persist,
            payload: __assign(__assign({}, body), { toUserId: toUserId }),
        };
        this.store$.dispatch(action);
        // Once the store is updated then we can let everyone know.
        this.fromServerStream$.next(event);
    };
    /* We buffer the user subscriptions and unsubscriptions by 1 second
     * to reduce backend load.
     * It's possible to have subscribed and unsubscriped within that buffer
     * so we remove such sub/unsub (and reverse) manually.
     */
    WebSocketService.prototype.subscribeToEvents = function () {
        // Subscribe to existing subscriptions
        var existingSubscriptions = this.onlineOfflineSubscriptions.values();
        if (existingSubscriptions.length) {
            this.sendEvent(generateOnlineOfflineSub(existingSubscriptions));
        }
        if (this.channelSubscriptions.size) {
            this.sendEvent(generateChannelSubMessage(Array.from(this.channelSubscriptions)));
        }
        this.sendEvent(generateOnlineOfflineSub(this.onlineOfflineSubscriptions.values()));
    };
    WebSocketService.prototype.subscribeOnlineOffline = function (userIds$) {
        var _this = this;
        // Watch the user id stream, calculate watch for changes,
        // subscribe/unsubscribe to any additions/removals respectively
        // and return the user status for the user who is subscribed to.
        // We add `undefined` to the end so we can get the last unsub.
        var incomingUserIds$ = Rx.concat(userIds$.pipe(distinctUntilChanged(), map(function (ids) { return ids.map(function (id) { return toNumber(id); }); }), startWithEmptyList()), Rx.of([])).pipe(pairwise());
        // Work out which user ids are just added to this subscription.
        var addedUserIds$ = incomingUserIds$.pipe(map(function (_a) {
            var previous = _a[0], current = _a[1];
            return setDiff(current, previous);
        }));
        // Work out which user ids are just removed to this subscription.
        var removedUserIds$ = incomingUserIds$.pipe(map(function (_a) {
            var previous = _a[0], current = _a[1];
            return setDiff(previous, current);
        }));
        // Add new ids to subscription counts and return ones newly added to the global collection
        var newlyAddedUserIds$ = addedUserIds$.pipe(map(function (ids) {
            return ids.filter(function (id) {
                _this.onlineOfflineSubscriptions.add(id);
                return _this.onlineOfflineSubscriptions.multiplicity(id) === 1;
            });
        }));
        // Add new ids to subscription counts and return ones newly added to the global collection
        var newlyRemovedUserIds$ = removedUserIds$.pipe(map(function (ids) {
            return ids.filter(function (id) {
                if (_this.onlineOfflineSubscriptions.multiplicity(id) === 0) {
                    console.warn("Tried removing user " + id + " who is already removed.");
                }
                _this.onlineOfflineSubscriptions.remove(id);
                return _this.onlineOfflineSubscriptions.multiplicity(id) === 0;
            });
        }));
        // Send websocket events and return an Rx.Observable.never()
        return Rx.NEVER.pipe(withLatestFrom(newlyAddedUserIds$.pipe(tap(function (ids) {
            if (_this.open && ids.length) {
                _this.sendEvent(generateOnlineOfflineSub(ids));
            }
        })), newlyRemovedUserIds$.pipe(tap(function (ids) {
            if (_this.open && ids.length) {
                _this.sendEvent(generateOnlineOfflineUnsub(ids));
            }
        }))));
    };
    WebSocketService.prototype.setChannels = function (channels) {
        var currentSubscriptions = Array.from(this.channelSubscriptions);
        var arrayDiff = function (a, b) {
            return a.filter(function (x) { return !b.includes(x); });
        };
        this.unsubscribeChannels(arrayDiff(currentSubscriptions, channels));
        this.subscribeChannels(arrayDiff(channels, currentSubscriptions));
    };
    WebSocketService.prototype.subscribeChannels = function (channels) {
        var _this = this;
        if (channels.length) {
            if (this.open) {
                this.sendEvent(generateChannelSubMessage(channels));
            }
            channels.forEach(function (channel) { return _this.channelSubscriptions.add(channel); });
        }
    };
    WebSocketService.prototype.unsubscribeChannels = function (channels) {
        var _this = this;
        if (channels.length) {
            channels.forEach(function (channel) { return _this.channelSubscriptions.delete(channel); });
            /* TODO: Do this properly.
             * Unfortunately the websocket backend doesn't support unsubscribing
             * and so we need to disconnect and reconnect the websocket to unsub :(
             *
             * Ref T35544
             */
            if (this.unsubDebounceTime) {
                clearTimeout(this.unsubDebounceTime);
            }
            this.unsubDebounceTime = this.timeUtils.setTimeout(function () {
                _this.unsubDebounceTime = undefined;
                _this.ngZone.runOutsideAngular(function () {
                    _this.sock.close();
                });
                _this.timeUtils.setTimeout(function () { return _this.create(); }, 1000);
                // We should also disconnect the other websocket that libnotify has.
                var w = window;
                if (w.libnotify && w.libnotify.close) {
                    w.libnotify.close();
                }
            }, 5000);
        }
    };
    return WebSocketService;
}());
export { WebSocketService };
